import { createContext, useContext, useState, useEffect, useCallback } from 'react'

const ThemeContext = createContext(null)

const DURATION      = 4000                          // ms — must match CSS 2s
const EASING        = 'cubic-bezier(0.7, 0, 0.3, 1)'
const FEATHER_RATIO = 0.12                          // feather band = 12% of max radius

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('portfolio-theme') || 'dark'
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('portfolio-theme', theme)
  }, [theme])

  const toggleTheme = useCallback((originX, originY, targetTheme = null) => {
    const x = originX ?? window.innerWidth  / 2
    const y = originY ?? window.innerHeight / 2
    const cycle = ['dark', 'light', 'ocean']
    const newTheme = targetTheme ?? cycle[(cycle.indexOf(theme) + 1) % cycle.length]
    if (newTheme === theme) return

    // Distance from click to the farthest viewport corner — guarantees full coverage
    const maxRadius = Math.ceil(Math.max(
      Math.hypot(x,                     y),
      Math.hypot(window.innerWidth  - x, y),
      Math.hypot(x,                     window.innerHeight - y),
      Math.hypot(window.innerWidth  - x, window.innerHeight - y),
    )) + 50

    // Feathered soft-edge band width (10-15 % of radius feels premium)
    const feather = Math.round(maxRadius * FEATHER_RATIO)

    // Expose geometry to CSS — used by Path A keyframes and as defaults for B
    const root = document.documentElement
    root.style.setProperty('--ripple-x',       `${x}px`)
    root.style.setProperty('--ripple-y',       `${y}px`)
    root.style.setProperty('--ripple-feather', `${feather}px`)

    // ─────────────────────────────────────────────────────────────
    // PATH A  View Transitions API  (Chrome 111+, Safari 18+, Edge 111+)
    //
    // The browser snapshots both old and new frames as GPU textures.
    // ::view-transition-new(root) in themes.scss has a mask-image that
    // references @property --ripple-r, which the keyframe animates
    // 0px → 3000px.  Feathered edge comes from the radial-gradient.
    // ─────────────────────────────────────────────────────────────
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        root.setAttribute('data-theme', newTheme)
        setTheme(newTheme)
        localStorage.setItem('portfolio-theme', newTheme)
      })
      return
    }

    // Snapshot the current page background before switching
    const oldBg =
      getComputedStyle(root).getPropertyValue('--bg-page').trim() || '#0A0A0D'

    // Switch theme immediately — will be hidden under the overlay
    root.setAttribute('data-theme', newTheme)
    setTheme(newTheme)
    localStorage.setItem('portfolio-theme', newTheme)

    const overlay = document.createElement('div')
    overlay.setAttribute('aria-hidden', 'true')

    // ─────────────────────────────────────────────────────────────
    // PATH B  @property mask-image  (Chrome 85+, Safari 15.4+, Edge 85+)
    //
    // The overlay is painted with the OLD background and covers the screen.
    // Its mask-image has a transparent hole at the button that grows as
    // @property --ripple-r transitions from 0 → maxRadius.  This reveals
    // the new theme beneath — identical look to Path A, different plumbing.
    //
    // Gradient direction: transparent = hole (new theme shows) · black = old theme
    // ─────────────────────────────────────────────────────────────
    const supportsAtProperty =
      typeof CSS !== 'undefined' && typeof CSS.registerProperty === 'function'

    if (supportsAtProperty) {
      overlay.style.cssText = `
        position: fixed; inset: 0;
        background: ${oldBg};
        z-index: 999999;
        pointer-events: none;
        --ripple-r: 0px;
        mask-image: radial-gradient(
          circle at ${x}px ${y}px,
          transparent calc(var(--ripple-r) - ${feather}px),
          black       var(--ripple-r)
        );
        -webkit-mask-image: radial-gradient(
          circle at ${x}px ${y}px,
          transparent calc(var(--ripple-r) - ${feather}px),
          black       var(--ripple-r)
        );
        transition: --ripple-r ${DURATION}ms ${EASING}; /* 2s */
      `
      document.body.appendChild(overlay)

      // Two rAF frames let the browser paint the overlay before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          overlay.style.setProperty('--ripple-r', `${maxRadius}px`)
        })
      })

    // ─────────────────────────────────────────────────────────────
    // PATH C  clip-path overlay  (Firefox — no @property, no VT API)
    //
    // Overlay starts covering the full screen and shrinks back into the
    // button via clip-path.  No soft edge, but the motion is identical.
    // ─────────────────────────────────────────────────────────────
    } else {
      overlay.style.cssText = `
        position: fixed; inset: 0;
        background: ${oldBg};
        z-index: 999999;
        pointer-events: none;
        clip-path: circle(${maxRadius + feather}px at ${x}px ${y}px);
        transition: clip-path ${DURATION}ms ${EASING};
      `
      document.body.appendChild(overlay)

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          overlay.style.clipPath = `circle(0px at ${x}px ${y}px)`
        })
      })
    }

    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true })
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
